/**
 * The per-channel diffusion board (RES-3, VAL-8) — one card per opened channel (comptoir / portail /
 * novera) with its live statut, detail line, and the gated actions:
 *   - "Délivrer" (POST /results/canaux/:id/delivrer) — for en_attente / libere channels. NOVA-055: the
 *     VAL-8 patient-channel hold is DERIVED SERVER-SIDE from the demande's open critical escalations (no
 *     operator toggle); a held channel comes back `statut: "retenu"` and the card shows a critical banner.
 *   - "Libérer" (POST /results/canaux/:id/liberer) — lifts a `retenu` (quarantine) channel early.
 * Every action is gated on results.canal.release via useCan (server re-checks). 5-state aware: empty
 * (no channels), and the parent owns loading/error/offline for the CR fetch.
 */
import { useState } from "react";
import { Badge, Button, Card, EmptyState } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { useToast } from "@/ui";
import type { CanalDiffusion, CanalStatut, CanalType } from "../types";
import { RESULTS_CAPS } from "../caps";
import { useDeliverChannel, useReleaseChannel } from "../hooks";
import { formatDateTime } from "../format";
import { StatutCanalBadge } from "./StatusBadges";
import { ConfirmModal } from "./ConfirmModal";

const CANAL_ORDER: Record<CanalType, number> = { comptoir: 0, portail: 1, novera: 2 };
/** comptoir = presence-based; remote channels surface the consent caveat. */
const REMOTE: ReadonlySet<CanalType> = new Set<CanalType>(["portail", "novera"]);

/** A channel can be (re)delivered when it is awaiting or has been released from a hold. */
function canDeliver(statut: CanalStatut): boolean {
  return statut === "en_attente" || statut === "libere";
}

interface PendingAction {
  kind: "deliver" | "release";
  canal: CanalDiffusion;
}

export function DiffusionBoard({
  compteRenduId,
  canaux,
}: {
  compteRenduId: string;
  canaux: ReadonlyArray<CanalDiffusion>;
}) {
  const { t } = useT();
  const toast = useToast();
  const online = useUiStore((s) => s.online);
  const canRelease = useCan(RESULTS_CAPS.release);

  const deliver = useDeliverChannel(compteRenduId);
  const release = useReleaseChannel(compteRenduId);

  const [pending, setPending] = useState<PendingAction | null>(null);

  if (canaux.length === 0) {
    return <EmptyState glyph="▤" title={t("results.diffusion.empty")} />;
  }

  const ordered = [...canaux].sort((a, b) => CANAL_ORDER[a.canal] - CANAL_ORDER[b.canal]);
  const busy = deliver.isPending || release.isPending;

  const runConfirmed = () => {
    if (!pending) return;
    if (pending.kind === "deliver") {
      deliver.mutate(
        { canalId: pending.canal.id },
        {
          onSuccess: (row) => {
            setPending(null);
            toast.success(
              t("results.canal.deliverSuccess", {
                canal: t(`results.canal.${row.canal}`),
                statut: t(`results.statut.${row.statut}`),
              }),
            );
          },
          onError: (err) => {
            setPending(null);
            toast.error(err.message || t("results.canal.deliverError"));
          },
        },
      );
    } else {
      release.mutate(
        { canalId: pending.canal.id },
        {
          onSuccess: (row) => {
            setPending(null);
            toast.success(t("results.canal.releaseSuccess", { canal: t(`results.canal.${row.canal}`) }));
          },
          onError: (err) => {
            setPending(null);
            toast.error(err.message || t("results.canal.releaseError"));
          },
        },
      );
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "var(--nv-s-4)",
        }}
      >
        {ordered.map((canal) => {
          const remote = REMOTE.has(canal.canal);
          const deliverable = canDeliver(canal.statut);
          const releasable = canal.statut === "retenu";
          return (
            <Card key={canal.id} title={t(`results.canal.${canal.canal}`)}>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
                  <StatutCanalBadge statut={canal.statut} />
                  {remote && (
                    <Badge tone="info" glyph="⛓">
                      {t("results.canal.remote")}
                    </Badge>
                  )}
                </div>

                <p style={{ margin: 0, color: "var(--nv-slate)", fontSize: "var(--nv-fs-caption)" }}>
                  {t(`results.canal.${canal.canal}.desc`)}
                </p>

                {/* detail line — delivered / held / failure motif */}
                <ChannelDetail canal={canal} />

                {/* NOVA-055 — the server-derived VAL-8 hold banner: a `retenu` channel was withheld because
                    the demande carries an un-acked critical (the hold is derived server-side, not toggled). */}
                {canal.statut === "retenu" && (
                  <p
                    role="alert"
                    style={{
                      margin: 0,
                      padding: "var(--nv-s-2) var(--nv-s-3)",
                      borderRadius: "var(--nv-r-sm)",
                      background: "var(--nv-tint-high)",
                      color: "var(--nv-hold)",
                      fontSize: "var(--nv-fs-caption)",
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--nv-s-2)",
                    }}
                  >
                    <span aria-hidden>⚠</span>
                    {t("results.canal.criticalUnacked")}
                  </p>
                )}

                <div style={{ display: "flex", gap: "var(--nv-s-2)", marginBlockStart: "auto" }}>
                  {canRelease && deliverable && (
                    <Button
                      size="sm"
                      onClick={() => setPending({ kind: "deliver", canal })}
                      disabled={!online || busy}
                      leadingIcon="➤"
                    >
                      {t("results.canal.deliver")}
                    </Button>
                  )}
                  {canRelease && releasable && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setPending({ kind: "release", canal })}
                      disabled={!online || busy}
                      leadingIcon="▷"
                    >
                      {t("results.canal.release")}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <ConfirmModal
        open={pending?.kind === "deliver"}
        title={t("results.canal.deliverConfirmTitle")}
        body={
          pending
            ? t("results.canal.deliverConfirmBody", { canal: t(`results.canal.${pending.canal.canal}`) })
            : ""
        }
        confirmLabel={t("results.canal.deliver")}
        busy={deliver.isPending}
        onConfirm={runConfirmed}
        onClose={() => !deliver.isPending && setPending(null)}
      />
      <ConfirmModal
        open={pending?.kind === "release"}
        title={t("results.canal.releaseConfirmTitle")}
        body={
          pending
            ? t("results.canal.releaseConfirmBody", { canal: t(`results.canal.${pending.canal.canal}`) })
            : ""
        }
        confirmLabel={t("results.canal.release")}
        confirmVariant="secondary"
        busy={release.isPending}
        onConfirm={runConfirmed}
        onClose={() => !release.isPending && setPending(null)}
      />
    </div>
  );
}

/** The per-channel detail line (delivered time / held-until / failure motif). */
function ChannelDetail({ canal }: { canal: CanalDiffusion }) {
  const { t, locale } = useT();
  if (canal.statut === "delivre" && canal.delivered_at) {
    return (
      <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-slate)" }}>
        {t("results.statut.deliveredAt", { date: formatDateTime(canal.delivered_at, locale) })}
      </p>
    );
  }
  if (canal.statut === "retenu" && canal.libere_apres_at) {
    return (
      <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-hold)" }}>
        {t("results.statut.heldUntil", { date: formatDateTime(canal.libere_apres_at, locale) })}
      </p>
    );
  }
  if (canal.statut === "echec" && canal.motif) {
    const known = canal.motif === "consentement_absent";
    return (
      <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-danger)" }}>
        {known ? t("results.motif.consentement_absent") : t("results.statut.motif", { motif: canal.motif })}
      </p>
    );
  }
  return null;
}
